import { useEffect, useMemo, useState } from "react"
import { useParams, useLocation } from "react-router-dom"
import { useDispatch, useSelector } from 'react-redux'

import { Loading, SectionHeading } from "../../components/common"
import { useForm, useFormErrors, useNav, useNavigateWithFilters, useUnsavedChangesGuard } from "../../components/hooks"
import { useLista } from "../../components/hooks/useLista"
import { VALIDATION_MESSAGE } from "../../consts/validationMessage"
import { APIS } from "../../config/apis"
import { ALERT_TYPE, OPERATION_MODE, REQUEST_METHOD, ShowToastMessage, TRIBUTO_TYPE, dataTaggerActionClear, dataTaggerActionSet } from '@laramie/ui';
import { ServerRequest } from "../../utils/apiweb"
import { CondicionesAplicacion, DescuentosAplicables, InfoAdicional, TasasPlan } from "./components"
import Definicion from "./components/Definicion"
import { formatDataForPut, initialFormValues, initialOtherValues, TIPOS_VINCULO_CUENTA } from "./utils"
import { getDateId } from "../../utils/convert"
import { CloneObject } from "../../utils/helpers"

const PagoContadoDefinicionView = () => {
    const navigate = useNav()
    const params = useParams()
    const location = useLocation()
    const previousFilters = location.state?.filters
    const navigateWithFilters = useNavigateWithFilters(() => previousFilters || null)

    const dispatch = useDispatch()
    const dataTagger = useSelector(state => state.dataTagger.data)

    const [pendingChange, setPendingChange] = useState(false);

    const [formValues, handleInput, , setFormValues] = useForm(initialFormValues)
    const [otherValues, setOtherValues] = useState(initialOtherValues)
    const [idUsuarioCreacion, setIdUsuarioCreacion] = useState(null)
    const [processKey, setProcessKey] = useState(`PagoContadoDefinicion_${params.id??0}_${getDateId()}`)
    const [loading, setLoading] = useState(false)
    const [utilizada, setUtilizada] = useState(false)
    const [fechaPrimeraUtilizacion, setFechaPrimeraUtilizacion] = useState(null)
    const [accordions, setAccordions] = useState({
        definicion: true,
        tasasPlan: false,
        condicionesAplicacion: false,
        infoAdicional: false,
    })

    const {allowNavigation} = useUnsavedChangesGuard(pendingChange);

    const getErrorsAlta = () => {
        const errors = {}

        if (formValues.codigo.length < 1) errors.codigo = VALIDATION_MESSAGE.REQUIRED
        if (formValues.descripcion.length < 1) errors.descripcion = VALIDATION_MESSAGE.REQUIRED
        if (formValues.idTipoPlanPago === 0) errors.idTipoPlanPago = VALIDATION_MESSAGE.REQUIRED_OPTION
        if (formValues.idTipoTributo === 0) errors.idTipoTributo = VALIDATION_MESSAGE.REQUIRED_OPTION
        if (formValues.idTipoVencimiento <= 0) errors.idTipoVencimiento = VALIDATION_MESSAGE.REQUIRED_OPTION

        return errors
    }

    const getErrors = () => {
        const errors = getErrorsAlta()

        if (formValues.idViaConsolidacion <= 0) errors.idViaConsolidacion = VALIDATION_MESSAGE.REQUIRED_OPTION
        if (formValues.idEstadoPagoContadoDefinicion <= 0) errors.idEstadoPagoContadoDefinicion = VALIDATION_MESSAGE.REQUIRED_OPTION
        if (formValues.fechaDesde === null) errors.fechaDesde = VALIDATION_MESSAGE.REQUIRED_DATE
        if (formValues.idTasaPagoContado <= 0) errors.idTasaPagoContado = VALIDATION_MESSAGE.REQUIRED_OPTION
        if (formValues.idSubTasaPagoContado <= 0) errors.idSubTasaPagoContado = VALIDATION_MESSAGE.REQUIRED_OPTION
        if (formValues.idTipoAlcanceTemporal <= 0) errors.idTipoAlcanceTemporal = VALIDATION_MESSAGE.REQUIRED_OPTION

        return errors
    }

    const { errorOf: errorOfAlta, validate: validateAlta } = useFormErrors(getErrorsAlta)
    const { errorOf: errorOfModificacion, validate: validateModificacion } = useFormErrors(getErrors)

    const errorOf = (name) => errorOfModificacion(name) ?? errorOfAlta(name)

    const openAccordion = (field) => setAccordions(prev => ({ ...prev, [field]: true }))

    const abrirAccordionDelError = (errors) => {
        if (errors.codigo || errors.descripcion || errors.idTipoPlanPago || errors.idTipoTributo ||
            errors.idEstadoPagoContadoDefinicion || errors.idTipoVencimiento || errors.fechaDesde || errors.idViaConsolidacion) openAccordion('definicion')
        if (errors.idTasaPagoContado || errors.idSubTasaPagoContado) openAccordion('tasasPlan')
        if (errors.idTipoAlcanceTemporal) openAccordion('condicionesAplicacion')
    }

    const [getListLista, , listaReady] = useLista({
        listas: Object.values(TIPOS_VINCULO_CUENTA),
        onLoaded: (listas, isSuccess, error) => {
            if (!isSuccess) ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error)
        },
        memo: {
            key: 'TipoVinculos',
            timeout: 0
        },
    })

    const tiposVinculoCuenta = useMemo(
        () => getListLista(TIPOS_VINCULO_CUENTA[formValues.idTipoTributo]),
        [formValues.idTipoTributo, listaReady]
    )

    // los vínculos de otro tributo no se ven en la pantalla, así que tampoco cuentan como relación cargada.
    // si la lista no llegó a cargar dejamos pasar: el webapi la resuelve por su cuenta y valida lo mismo
    const tieneRelacionesPermitidas = () => otherValues.pagosContadoDefinicionTipoVinculoCuenta
        .some(item => item.state !== 'r' && (!listaReady || tiposVinculoCuenta.some(tipo => tipo.id === item.idTipoVinculoCuenta)))

    const onDataReceived = (data) => {
        if (data.pagoContadoDefinicion.fechaDesde) data.pagoContadoDefinicion.fechaDesde = new Date(data.pagoContadoDefinicion.fechaDesde);
        if (data.pagoContadoDefinicion.fechaHasta) data.pagoContadoDefinicion.fechaHasta = new Date(data.pagoContadoDefinicion.fechaHasta);
        if (data.pagoContadoDefinicion.fechaDesdeAlcanceTemporal) data.pagoContadoDefinicion.fechaDesdeAlcanceTemporal = new Date(data.pagoContadoDefinicion.fechaDesdeAlcanceTemporal);
        if (data.pagoContadoDefinicion.fechaHastaAlcanceTemporal) data.pagoContadoDefinicion.fechaHastaAlcanceTemporal = new Date(data.pagoContadoDefinicion.fechaHastaAlcanceTemporal);
        if (data.pagoContadoDefinicion.fechaCreacion) data.pagoContadoDefinicion.fechaCreacion = new Date(data.pagoContadoDefinicion.fechaCreacion);
        data.archivos.forEach(x => {
            if (x.fecha) x.fecha = new Date(x.fecha);
        });
        data.observaciones.forEach(x => {
            if (x.fecha) x.fecha = new Date(x.fecha);
        });

        const { utilizada, fechaPrimeraUtilizacion, ...dataDefinicion } = data

        setFormValues(data.pagoContadoDefinicion)
        setIdUsuarioCreacion(data.pagoContadoDefinicion.idUsuarioCreacion);
        setOtherValues({...dataDefinicion, pagoContadoDefinicion: undefined})

        dispatch(dataTaggerActionSet(processKey, {
            Archivo: data.archivos,
            Observacion: data.observaciones,
            Etiqueta: data.etiquetas
        }))
    }

    const onBadDataReceived = (error) => {
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando respuesta: ' + error)
        setLoading(false)
    }

    const onNoSuccess = (response) => {
        response.json()
            .then((error) => {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error.message);
                setLoading(false)
            })
            .catch(onBadDataReceived)
    }

    const onError = (error) => {
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando solicitud: ' + error.message);
        setLoading(false)
    }

    useEffect(() => {
        if (params.mode !== OPERATION_MODE.NEW) {
            const onSuccess = (res) => {
                res.json()
                    .then(data => {
                        setUtilizada(data.utilizada === true)
                        onDataReceived(data)
                        setFechaPrimeraUtilizacion(data.fechaPrimeraUtilizacion ? new Date(data.fechaPrimeraUtilizacion) : null)
                    })
                    .catch(onBadDataReceived)
                setLoading(false)
            }

            setLoading(true)
            ServerRequest(
                REQUEST_METHOD.GET,
                null,
                true,
                APIS.URLS.PAGO_CONTADO_DEFINICION,
                `/${params.id}`,
                null,
                onSuccess,
                onNoSuccess,
                onError,
            )
        }
        else {
            dispatch(dataTaggerActionSet(processKey, {
                Archivo: [],
                Observacion: [],
                Etiqueta: []
            }))
        }
    }, [])

    const handleInputProxy = (event) => {
        handleInput(event);
        setPendingChange(true);
    }   

    const add = () => {
        if (!validateAlta()) {
            openAccordion('definicion')
            return
        }

        if (!tieneRelacionesPermitidas()) {
            ShowToastMessage(ALERT_TYPE.ALERT_WARNING, 'Falta definir las relaciones permitidas')
            openAccordion('definicion')
            return
        }

        const onSuccess = (res) => {
            res.json()
                .then(obj => {
                    setPendingChange(false);
                    ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, "Definición de pago contado ingresado correctamente", () => {
                        dispatch(dataTaggerActionClear(processKey))
                        setLoading(false)
                        allowNavigation()
                        navigate({
                            to: `/pago-contado-definicion/edit/${obj.pagoContadoDefinicion.id}`,
                            refresh: true,
                            replace: true,
                        })
                    })
                })
                .catch(onBadDataReceived);
        }

        const pagoContadoDefinicion = {...formValues, noAplicaDeudoresDDJJ: [TRIBUTO_TYPE.COMERCIOS, TRIBUTO_TYPE.FONDEADEROS].includes(formValues.idTipoTributo) && formValues.noAplicaDeudoresDDJJ === true};

        setLoading(true)
        ServerRequest(
            REQUEST_METHOD.POST,
            null,
            true,
            APIS.URLS.PAGO_CONTADO_DEFINICION,
            null,
            { pagoContadoDefinicion: pagoContadoDefinicion, ...otherValues },
            onSuccess,
            onNoSuccess,
            onError,
        )
    }

    const modify = () => {
        abrirAccordionDelError(getErrors())

        if (!validateModificacion())
            return

        if (!tieneRelacionesPermitidas()) {
            ShowToastMessage(ALERT_TYPE.ALERT_WARNING, 'Falta definir las relaciones permitidas')
            openAccordion('definicion')
            return
        }

        const onSuccess = (res) => {
            res.json().then(obj => {
                onDataReceived(obj)
                setPendingChange(false);
                ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, "Definición de pago contado ingresado correctamente")
            })
            .catch(onBadDataReceived)
            setLoading(false)
        }

        setLoading(true)

        const partialOtherValues = CloneObject(otherValues);
        const allOtherValues = {...partialOtherValues,
            archivos: (dataTagger[processKey]) ? dataTagger[processKey].Archivo : [],
            observaciones: (dataTagger[processKey]) ? dataTagger[processKey].Observacion : [],
            etiquetas: (dataTagger[processKey]) ? dataTagger[processKey].Etiqueta : []
        }

        const pagoContadoDefinicion = {...formValues, noAplicaDeudoresDDJJ: [TRIBUTO_TYPE.COMERCIOS, TRIBUTO_TYPE.FONDEADEROS].includes(formValues.idTipoTributo) && formValues.noAplicaDeudoresDDJJ === true};

        ServerRequest(
            REQUEST_METHOD.PUT,
            null,
            true,
            APIS.URLS.PAGO_CONTADO_DEFINICION,
            `/${params.id}`,
            formatDataForPut(pagoContadoDefinicion, allOtherValues),
            onSuccess,
            onNoSuccess,
            onError,
        )
    }

    const onClickVolver = () => {
        if (!pendingChange) dispatch(dataTaggerActionClear(processKey));
        navigateWithFilters(`/pagos-contados-definicion`);
    }

    const paramsSecciones = utilizada ? {...params, mode: OPERATION_MODE.VIEW} : params

    const buildSectionProps = (field) => ({
        toggle: () => setAccordions(prev => ({ ...prev, [field]: !prev[field] })),
        open: accordions[field],
        formValues,
        setFormValues,
        handleInputProxy,
        errorOf,
        otherValues,
        setOtherValues,
        params: paramsSecciones,
        tiposVinculoCuenta,
    })

    return (
        <div>
            <Loading visible={loading}></Loading>

            <SectionHeading
                titles={[
                    { title: 'Financiación' },
                    { title: 'Definición de pago contado', url: '/pagos-contados-definicion' },
                    { title: params.mode === OPERATION_MODE.NEW ? 'Nuevo' : `Código: ${formValues.codigo}`}
                ]}
            />

            <div className="m-top-20">
                <section className='section-accordion'>
                    <div className="m-top-20 m-bottom-20">  
                        {utilizada && (
                            <div className='row'>
                                <div className="col-12 m-bottom-20">
                                    <span className="busqueda-mensaje m-left-20">
                                        <span className="material-symbols-outlined" aria-hidden="true">warning</span>
                                        La definición ya fue utilizada por un recibo; solo se puede modificar la vigencia, la descripción y el estado
                                    </span>
                                </div>
                            </div>
                        )}
                        <Definicion {...buildSectionProps('definicion')} {...{params, setPendingChange, idUsuarioCreacion, utilizada, fechaPrimeraUtilizacion}} />
                        {params.mode !== OPERATION_MODE.NEW && <>
                            <TasasPlan {...buildSectionProps('tasasPlan')} />
                            <CondicionesAplicacion {...buildSectionProps('condicionesAplicacion')} {...{setPendingChange}}/>
                            <DescuentosAplicables {...buildSectionProps('descuentosAplicables')} />
                            <InfoAdicional {...buildSectionProps('infoAdicional')} params={params} idEntity={parseInt(params.id)} processKey={processKey} setPendingChange={setPendingChange}/>
                        </>}
                    </div>
                </section>
            </div>

            <footer className='footer footer-action'>
            <div className='footer-action-container'>
                <button className="btn back-button float-start" onClick={onClickVolver}>Volver</button>
                {params.mode !== OPERATION_MODE.VIEW && (
                    <button
                        className={pendingChange ? "btn action-button float-end" : "btn action-button btn-disabled float-end"}
                        onClick={params.mode === OPERATION_MODE.NEW ? add : modify}
                        disabled={!pendingChange}
                    >
                        {params.mode === OPERATION_MODE.NEW ? 'Siguiente' : 'Guardar'}
                    </button>
                )}
            </div>
        </footer>
        </div>
    )
}

export default PagoContadoDefinicionView
